import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChefHat, ScrollText, ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const sections = [
    {
      title: '1. Acceptance of Terms',
      content: `By downloading, installing, accessing, or using RecipeUp ("the Software"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you must not download, install, or use the Software.

These Terms constitute a legally binding agreement between you ("User," "you," or "your") and RecipeUp ("Company," "we," "us," or "our") governing your use of the Software.

We reserve the right to modify these Terms at any time. Your continued use of the Software after any such changes constitutes your acceptance of the new Terms. We encourage you to review these Terms periodically.`
    },
    {
      title: '2. License Grant',
      content: `Subject to your compliance with these Terms, RecipeUp grants you a limited, non-exclusive, non-transferable, revocable license to:

• Download and install the Software on Windows devices that you own or control
• Use the Software for personal, educational, or commercial purposes
• Make reasonable backup copies of the Software for archival purposes

This license is granted free of charge for the standard features of the Software.

The license granted herein does not include:
• The right to sublicense, sell, resell, or commercially distribute the Software
• The right to modify, adapt, translate, or create derivative works
• The right to reverse engineer, decompile, or disassemble the Software
• The right to remove or alter any proprietary notices or labels

This license is effective until terminated. Your rights under this license will terminate automatically without notice if you fail to comply with any of these Terms.`
    },
    {
      title: '3. User Responsibilities',
      content: `As a user of RecipeUp, you agree to:

Lawful Use:
• Use the Software only for lawful purposes and in compliance with all applicable laws
• Not use the Software in any way that could damage, disable, or impair our services

Content Responsibility:
• You are solely responsible for any custom recipes, meal plans, or data you create
• You must have the legal right to use any content you add to the Software
• You must not use the Software to infringe on any intellectual property rights

Security:
• Maintain the security of your device and the Software installation
• Not attempt to gain unauthorized access to any systems or networks
• Report any security vulnerabilities you discover to us promptly

Compliance:
• Comply with all applicable export control and sanctions laws
• Ensure your use complies with any industry-specific regulations applicable to you`
    },
    {
      title: '4. Intellectual Property Rights',
      content: `Ownership:
The Software, including all code, graphics, user interface, design elements, recipe database, documentation, and all intellectual property rights therein, are and shall remain the exclusive property of RecipeUp and its licensors.

Trademarks:
"RecipeUp," the RecipeUp logo, and all related names, logos, and slogans are trademarks of RecipeUp. You may not use these trademarks without our prior written permission.

User Content:
You retain all rights to custom recipes, meal plans, and other content you create using the Software. We claim no ownership or rights over your personal content. The Software stores your content locally, and we never access or store it.

Recipe Database:
The built-in recipe database is licensed for use within the Software only. You may not extract, reproduce, or redistribute the recipe database or any substantial portion thereof.

Feedback:
If you provide us with any feedback, suggestions, or ideas regarding the Software, you grant us a non-exclusive, worldwide, royalty-free license to use, modify, and incorporate such feedback into our products and services.`
    },
    {
      title: '5. Privacy and Data',
      content: `Your privacy is important to us. Our collection and use of information is governed by our Privacy Policy, which is incorporated into these Terms by reference.

Key Privacy Commitments:
• All recipe management and meal planning occurs locally on your device
• We do not upload, store, or access your personal recipe data
• We collect minimal data necessary to operate and improve the Software
• You can opt out of optional data collection at any time

By using the Software, you consent to our data practices as described in our Privacy Policy.`
    },
    {
      title: '6. Health and Nutrition Disclaimer',
      content: `IMPORTANT: RecipeUp provides nutritional information for informational purposes only. This information should NOT be used as a substitute for professional medical advice, diagnosis, or treatment.

• Nutritional data is approximate and may vary based on actual ingredients and preparation methods
• Always consult with a qualified healthcare provider regarding dietary needs, food allergies, or health conditions
• RecipeUp is not responsible for any health outcomes related to recipes or nutritional information provided
• Users with food allergies should independently verify all ingredient information
• The Software does not provide medical or dietary advice`
    },
    {
      title: '7. Disclaimer of Warranties',
      content: `THE SOFTWARE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.

TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, RECIPEUP DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:

• IMPLIED WARRANTIES OF MERCHANTABILITY
• FITNESS FOR A PARTICULAR PURPOSE
• NON-INFRINGEMENT
• ACCURACY OR COMPLETENESS OF CONTENT (INCLUDING RECIPES AND NUTRITIONAL DATA)

We do not warrant that:
• The Software will meet your specific requirements
• The Software will be uninterrupted, timely, secure, or error-free
• Nutritional information will be perfectly accurate
• Any errors in the Software will be corrected

Some jurisdictions do not allow the exclusion of certain warranties, so some of the above exclusions may not apply to you.`
    },
    {
      title: '8. Limitation of Liability',
      content: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL RECIPEUP, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, LICENSORS, OR SERVICE PROVIDERS BE LIABLE FOR:

• Any indirect, incidental, special, consequential, or punitive damages
• Loss of profits, revenue, data, use, goodwill, or other intangible losses
• Damages resulting from unauthorized access to or use of the Software
• Damages resulting from any interruption or cessation of the Software
• Any health-related damages resulting from use of recipes or nutritional information

WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, OR ANY OTHER LEGAL THEORY.

IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT YOU PAID TO US, IF ANY, FOR THE SOFTWARE DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.`
    },
    {
      title: '9. Indemnification',
      content: `You agree to defend, indemnify, and hold harmless RecipeUp and its officers, directors, employees, agents, licensors, and service providers from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to:

• Your violation of these Terms
• Your use of the Software
• Your violation of any third-party rights, including intellectual property rights
• Any content you create using the Software
• Your violation of any applicable laws or regulations`
    },
    {
      title: '10. Termination',
      content: `Term:
These Terms are effective from your first use of the Software and continue until terminated.

Termination by You:
You may terminate these Terms at any time by uninstalling the Software and destroying all copies in your possession.

Termination by Us:
We may terminate or suspend your license immediately, without prior notice or liability, for any reason, including if you breach these Terms.

Effects of Termination:
Upon termination, your license to use the Software will immediately cease. You must uninstall the Software and destroy all copies. Sections of these Terms that by their nature should survive will continue to apply.`
    },
    {
      title: '11. Updates and Modifications',
      content: `Software Updates:
• We may release updates, patches, or new versions of the Software from time to time
• Updates may be downloaded automatically or require manual installation
• Updates may include new recipes, features, or bug fixes
• We are not obligated to provide any updates or support

Modification of Terms:
• We reserve the right to modify these Terms at any time
• Material changes will be communicated through the Software or our website
• Your continued use after changes constitutes acceptance of the modified Terms`
    },
    {
      title: '12. Governing Law and Dispute Resolution',
      content: `Governing Law:
These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.

Dispute Resolution:
Any dispute arising from or relating to these Terms or the Software shall be resolved as follows:

1. Informal Resolution: We encourage you to contact us first to seek an informal resolution.

2. Binding Arbitration: If informal resolution fails, disputes shall be resolved by binding arbitration administered by the American Arbitration Association.

3. Class Action Waiver: You agree to resolve disputes on an individual basis and waive any right to participate in class actions.

Jurisdiction:
For any matters not subject to arbitration, you consent to the exclusive jurisdiction of the state and federal courts located in Delaware.`
    },
    {
      title: '13. General Provisions',
      content: `Entire Agreement:
These Terms, together with our Privacy Policy, constitute the entire agreement between you and RecipeUp regarding the Software.

Severability:
If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.

Waiver:
Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.

Assignment:
You may not assign or transfer these Terms without our prior written consent. We may assign our rights and obligations without restriction.

Force Majeure:
We shall not be liable for any failure to perform due to causes beyond our reasonable control.`
    },
    {
      title: '14. Contact Information',
      content: `If you have any questions about these Terms of Service, please contact us:

Email: legal@recipeup.com
General Support: support@recipeup.com
Website: https://recipeup.com

Last Updated: March 2026`
    }
  ];

  return (
    <div className="min-h-screen bg-[#0c0806] text-white">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-amber-600/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-orange-600/10 rounded-full blur-[128px]" />
      </div>

      <nav className="relative z-50 px-6 py-5 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>RecipeUp</span>
          </Link>
          <Link to="/" className="text-white/60 hover:text-white transition-colors font-medium flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <header className="relative z-10 py-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mb-6"
          >
            <div className="p-4 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-2xl border border-white/10">
              <ScrollText className="w-8 h-8 text-amber-400" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">Terms of Service</h1>
              <p className="text-white/50 mt-2">Please read these terms carefully before using RecipeUp.</p>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="relative z-10 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-12"
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/[0.02] rounded-2xl border border-white/5 p-8"
              >
                <h2 className="text-2xl font-bold mb-4 text-white">{section.title}</h2>
                <div className="text-white/60 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </main>

      <footer className="relative z-10 py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>RecipeUp</span>
          </div>
          <div className="flex gap-8 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-white">Terms of Service</Link>
          </div>
          <p className="text-white/30 text-sm">© {new Date().getFullYear()} RecipeUp</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
